/**
 * numberUtils.js
 * Funções utilitárias para conversão e formatação de números
 */

/**
 * Converte um valor para número, lidando com diferentes formatos
 * @param {*} value - Valor a ser convertido para número
 * @param {number} defaultValue - Valor padrão caso a conversão falhe
 * @returns {number} - Valor numérico ou defaultValue
 */
export function parseNumber(value, defaultValue = 0) {
  if (value === null || value === undefined) return defaultValue;

  // Se já for um número, retorna ele mesmo
  if (typeof value === 'number') return value;

  // Se for string, converte para número
  if (typeof value === 'string') {
    // Remove caracteres não numéricos exceto ponto decimal
    const cleanValue = value.replace(/[^0-9.]/g, '');
    if (cleanValue === '') return defaultValue;

    const number = parseFloat(cleanValue);
    return isNaN(number) ? defaultValue : number;
  }

  return defaultValue;
}

/**
 * Formata um número para exibição com casas decimais fixas e separador de milhar
 * @param {number|string} value - Número a ser formatado
 * @param {number} decimalPlaces - Número de casas decimais
 * @param {string} decimalSeparator - Separador decimal
 * @param {string} thousandsSeparator - Separador de milhar
 * @returns {string} - String formatada
 */
export function formatNumber(
  value,
  decimalPlaces = 2,
  decimalSeparator = ',',
  thousandsSeparator = '.',
) {
  const number = parseNumber(value);

  // Usa o Intl.NumberFormat para formatação consistente
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })
    .format(number)
    .replace('.', decimalSeparator)
    .replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
}

/**
 * Converte um valor de área para hectares
 * @param {*} value - Valor em m² a ser convertido para hectares
 * @param {number} defaultValue - Valor padrão caso a conversão falhe
 * @returns {number} - Valor em hectares
 */
export function parseAreaToHectares(value, defaultValue = 0) {
  const areaInSquareMeters = parseNumber(value, defaultValue);
  return areaInSquareMeters / 10000; // 1 hectare = 10000 m²
}

/**
 * Formata um valor de área em hectares para exibição
 * @param {number|string} value - Área em hectares
 * @returns {string} - String formatada com unidade "ha"
 */
export function formatAreaHa(value) {
  return `${formatNumber(value, 2)} ha`;
}
